import { Controller, useWatch } from "react-hook-form";
import { Stack, Group, Divider, Tooltip } from "@mantine/core";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import useIsDesktop from "../../../../hooks/useIsDesktop";
import { CustomSwitch } from "../../../ui/CustomSwitch/CustomSwitch";
import CustomModal from "../../../ui/CustomModal/CustomModal";
import CustomTextInput from "../../../ui/CustomTextInput/CustomTextInput";
import CustomSelect from "../../../ui/CustomSelect/CustomSelect";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomLoader from "../../../ui/CustomLoader/CustomLoader";
import { validationSchemas } from "../hooks/useAccountForm";
import {
  useMobileMoneyProviderOptions,
  renderMobileMoneyOption,
} from "../../../shared/mobileMoneyProviders";
import { optionsFilterByLabel } from "../../../../utils/optionsFilter";

const {
  msisdn: msisdnValidator,
  provider: providerValidator,
  accountName: accountNameValidator,
} = validationSchemas.mobileMoneyValidators;

const AddMobileMoneyModal = ({
  opened,
  onClose,
  onSubmit,
  control,
  isSubmitting,
  handleMsisdnBlur,
  handleMsisdnChange,
  handleProviderChange,
  isVerifying,
  isVerified,
  isUnverified,
  verificationMessage,
}) => {
  const isDesktop = useIsDesktop();
  const mobileMoneyProviderOptions = useMobileMoneyProviderOptions();
  const provider = useWatch({ control, name: "provider" });
  const accountName = useWatch({ control, name: "accountName" });
  const fieldsLocked = isSubmitting || isVerifying;
  const isAddDisabled =
    isSubmitting || isVerifying || !isVerified || !accountName;

  const getDisabledReason = () => {
    if (!provider) return "Select a provider first";
    if (isVerifying) return "Verifying account, please wait";
    if (!isVerified) return "Verify the phone number to continue";
    return undefined;
  };
  const labelStyle = {
    label: {
      fontWeight: 600,
      color: "#1f2937",
      fontSize: "0.9rem",
      marginBottom: "4px",
    },
  };

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      title={
        <span className="text-lg font-semibold text-gray-900">
          Add Mobile Money Account
        </span>
      }
      centered
      radius="lg"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      styles={{ body: { padding: 0 } }}
      closeOnEscape={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      withCloseButton={!isSubmitting}
    >
      <div
        style={{
          maxHeight: "calc(90dvh - 170px)",
          overflowY: "auto",
          padding: "0 1rem 1rem",
        }}
      >
        <Divider mb="md" />
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="provider"
              control={control}
              rules={{
                validate: (v) =>
                  providerValidator(
                    v,
                    mobileMoneyProviderOptions.map((p) => p.value),
                  ) ?? true,
              }}
              render={({ field, fieldState }) => (
                <CustomSelect
                  label="Provider"
                  placeholder="Select provider"
                  data={mobileMoneyProviderOptions}
                  renderOption={renderMobileMoneyOption}
                  searchable
                  lockOnSelect
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v);
                    handleProviderChange();
                  }}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  radius="xl"
                  styles={labelStyle}
                  disabled={fieldsLocked}
                  withAsterisk
                  comboboxProps={{ withinPortal: false }}
                  filter={optionsFilterByLabel}
                />
              )}
            />

            <Controller
              name="msisdn"
              control={control}
              rules={{ validate: (v) => msisdnValidator(v) ?? true }}
              render={({ field, fieldState }) => (
                <div>
                  <CustomTextInput
                    label="MSISDN (Phone Number)"
                    placeholder={
                      provider ? "e.g. 254712345678" : "Select a provider first"
                    }
                    radius="xl"
                    styles={labelStyle}
                    disabled={fieldsLocked || !provider}
                    withAsterisk
                    rightSection={
                      isVerifying ? (
                        <CustomLoader size={14} color="success" />
                      ) : null
                    }
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      handleMsisdnChange();
                    }}
                    onBlur={() => {
                      field.onBlur();
                      handleMsisdnBlur(field.value, provider);
                    }}
                    error={fieldState.error?.message}
                  />
                  {isVerified && (
                    <div className="flex items-center gap-1.5 px-1 mt-1">
                      <FaCheckCircle
                        style={{ fontSize: 13 }}
                        className="text-green-500 shrink-0"
                      />
                      <span className="text-xs text-green-600 font-medium">
                        Account verified
                      </span>
                    </div>
                  )}
                  {isUnverified && (
                    <div className="flex items-center gap-1.5 px-1 mt-1">
                      <FaTimesCircle
                        style={{ fontSize: 13 }}
                        className="text-red-500 shrink-0"
                      />
                      <span className="text-xs text-red-500 font-medium">
                        {verificationMessage || "Could not verify account"}
                      </span>
                    </div>
                  )}
                </div>
              )}
            />

            <Controller
              name="accountName"
              control={control}
              rules={{ validate: (v) => accountNameValidator(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label="Account Name"
                  placeholder="Auto-populated after verification"
                  radius="xl"
                  styles={labelStyle}
                  disabled
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="isDefaultPayout"
              control={control}
              render={({ field }) => (
                <CustomSwitch
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                  activeLabel="IsDefaultPaymentMethod"
                  color="primary"
                  inactiveLabel="NotDefaultPaymentMethod"
                  disabled={isSubmitting}
                />
              )}
            />

            <Group
              justify={isDesktop ? "flex-end" : "stretch"}
              grow={!isDesktop}
              mt="md"
            >
              <CustomButton
                variant="default"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </CustomButton>
              <Tooltip
                label={getDisabledReason() ?? ""}
                withArrow
                position="top"
                disabled={!isAddDisabled || !getDisabledReason()}
              >
                <span className="block">
                  <CustomButton
                    type="submit"
                    disabled={isAddDisabled}
                    loading={isSubmitting}
                    fullWidth={!isDesktop}
                  >
                    Add Mobile Money Account
                  </CustomButton>
                </span>
              </Tooltip>
            </Group>
          </Stack>
        </form>
      </div>
    </CustomModal>
  );
};

export default AddMobileMoneyModal;
